import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { applyAppearance, cachedPreferences, formatWhen, type Preferences } from "./preferences";

/**
 * Preferences for the session and `useFormat()` — the one way a date or
 * time is rendered (profile template: `useFormat()` everywhere, by lint).
 * Until /v1/me exists (PR 5) the source is the browser's cache; the
 * provider's `update` writes both the attributes on <html> and the cache.
 */
interface PreferencesContextValue {
  preferences: Preferences;
  update: (patch: Partial<Preferences>) => void;
}

const PreferencesContext = createContext<PreferencesContextValue>({ preferences: cachedPreferences(), update: () => {} });

export function PreferencesProvider({ children, initial }: { children: ReactNode; initial?: Preferences }) {
  const [preferences, setPreferences] = useState<Preferences>(() => initial ?? cachedPreferences());
  useEffect(() => applyAppearance(preferences), [preferences]);
  // Stable, so an effect that adopts the server's preferences once /v1/me answers does not re-run on its own change.
  const update = useCallback((patch: Partial<Preferences>) => setPreferences((p) => (Object.entries(patch).every(([k, v]) => p[k as keyof Preferences] === v) ? p : { ...p, ...patch })), []);
  const value = useMemo(() => ({ preferences, update }), [preferences, update]);
  return <PreferencesContext.Provider value={value}>{children}</PreferencesContext.Provider>;
}

export function usePreferences(): PreferencesContextValue {
  return useContext(PreferencesContext);
}

export interface Format {
  when: (iso: string) => string;
  date: (iso: string) => string;
  locale: string;
  timeZone: string;
}

export function useFormat(): Format {
  const { preferences } = usePreferences();
  return useMemo(
    () => ({
      when: (iso: string) => formatWhen(iso, preferences),
      date: (iso: string) => formatWhen(iso, preferences, false),
      locale: typeof navigator !== "undefined" ? navigator.language : "en-US",
      timeZone: preferences.timeZone === "auto" ? Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC" : preferences.timeZone,
    }),
    [preferences],
  );
}
